import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Check, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/lang";
import { Seo, ORIGIN } from "@/components/Seo";
import { PROJECTS } from "@/content/site";
import { getLanding } from "@/content/serviceLandings";
import { Reveal, ParallaxImage } from "@/lib/motion";
import { LocaleLink as Link } from "@/i18n/routes";

export default function ServiceLanding({ data, locale }) {
  const { lang, setLang } = useLang();
  const navigate = useNavigate();
  const synced = useRef(false);

  // Force the page language to match the URL (deterministic for crawlers).
  useEffect(() => {
    synced.current = false;
    setLang(locale);
  }, [locale, setLang]);

  // Navigate to the counterpart URL ONLY when the user actively toggles the
  // language after the page has synced to its URL locale — never on first mount
  // (where context lang may still hold the previous/default value).
  useEffect(() => {
    if (lang === locale) {
      synced.current = true;
      return;
    }
    if (synced.current) navigate(data[lang].path);
  }, [lang, locale, data, navigate]);

  const c = data[locale];
  const relatedServices = (data.related.services || [])
    .map((key) => {
      const l = getLanding(key);
      return l ? { path: l[locale].path, label: l[locale].h1 } : null;
    })
    .filter(Boolean);
  const relatedProjects = PROJECTS.filter((p) => data.related.projects.includes(p.slug));

  const contactLabel = locale === "de" ? "Projekt starten" : "Start a project";
  const deliverLabel = locale === "de" ? "Was du bekommst" : "What you get";
  const faqLabel = locale === "de" ? "Häufige Fragen" : "Frequently asked questions";
  const workLabel = locale === "de" ? "Passende Projekte" : "Related work";
  const servicesLabel = locale === "de" ? "Weitere Leistungen" : "More services";
  const backLabel = locale === "de" ? "Alle Leistungen" : "All services";

  const alternates = [
    { hrefLang: "de", href: ORIGIN + data.de.path },
    { hrefLang: "en", href: ORIGIN + data.en.path },
    { hrefLang: "x-default", href: ORIGIN + data.de.path },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: c.serviceName,
        serviceType: c.serviceType,
        description: c.description,
        url: ORIGIN + c.path,
        provider: { "@type": "Organization", name: "WERKVERDUNG", url: ORIGIN },
        areaServed: locale === "de" ? "DE" : "Worldwide",
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: ORIGIN + "/" },
          { "@type": "ListItem", position: 2, name: locale === "de" ? "Leistungen" : "Services", item: ORIGIN + "/services" },
          { "@type": "ListItem", position: 3, name: c.h1, item: ORIGIN + c.path },
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: c.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <div data-testid={`service-landing-${data.key}-${locale}`}>
      <Seo
        title={c.title}
        description={c.description}
        path={c.path}
        lang={locale}
        image={data.image}
        alternates={alternates}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="pt-36 md:pt-44 pb-14 border-b border-border">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <Link to="/services" data-testid="landing-back" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-stone hover:text-bronze transition-colors mb-10">
            <ArrowRight size={13} className="rotate-180" /> {backLabel}
          </Link>
          <Reveal>
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-bronze">— {c.eyebrow}</span>
            <h1 className="mt-6 font-serif text-5xl md:text-7xl lg:text-8xl tracking-tight text-ink leading-[0.98] max-w-4xl">
              {c.h1}
            </h1>
            <p className="mt-8 max-w-2xl text-lg md:text-xl leading-relaxed text-stone">{c.intro}</p>
            <Link
              to="/contact"
              data-testid="landing-hero-cta"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-bronze text-paper px-8 py-4 text-sm font-semibold hover:bg-[#f0be6e] transition-colors duration-300"
            >
              {contactLabel} <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Hero image */}
      <section className="py-14 md:py-20">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <ParallaxImage src={data.image} alt={c.h1} className="w-full aspect-[16/9] bg-secondary" strength={28} />
        </div>
      </section>

      {/* Content sections */}
      <section className="pb-8 md:pb-12">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-8 lg:col-start-3 space-y-14">
              {c.sections.map((s, i) => (
                <Reveal key={i}>
                  <div>
                    <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-ink">{s.h2}</h2>
                    <p className="mt-4 text-base md:text-lg leading-relaxed text-stone">{s.body}</p>
                  </div>
                </Reveal>
              ))}

              {/* Deliverables */}
              <Reveal>
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-widest text-bronze mb-4">{deliverLabel}</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {c.deliver.map((d, k) => (
                      <li key={k} className="flex items-center gap-2.5 text-base text-ink">
                        <Check size={16} className="text-bronze shrink-0" /> {d}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 border-t border-border">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-8 lg:col-start-3">
              <p className="font-mono text-[11px] uppercase tracking-widest text-bronze mb-8">{faqLabel}</p>
              <div className="divide-y divide-border border-t border-border">
                {c.faq.map((f, i) => (
                  <Reveal key={i}>
                    <div className="py-7">
                      <h3 className="font-serif text-xl md:text-2xl text-ink">{f.q}</h3>
                      <p className="mt-3 text-base leading-relaxed text-stone">{f.a}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related work */}
      {relatedProjects.length > 0 && (
        <section className="py-16 md:py-24 border-t border-border">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
            <p className="font-mono text-[11px] uppercase tracking-widest text-bronze mb-8">{workLabel}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {relatedProjects.map((p) => (
                <Link key={p.slug} to={`/work/${p.slug}`} data-testid={`landing-project-${p.slug}`} className="group block">
                  <div className="overflow-hidden bg-secondary aspect-[4/3]">
                    <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
                  </div>
                  <h3 className="mt-4 font-serif text-2xl text-ink group-hover:text-bronze transition-colors">{p.title}</h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related services */}
      {relatedServices.length > 0 && (
        <section className="py-14 border-t border-border">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
            <p className="font-mono text-[11px] uppercase tracking-widest text-bronze mb-6">{servicesLabel}</p>
            <div className="flex flex-wrap gap-x-8 gap-y-3">
              {relatedServices.map((s) => (
                <Link
                  key={s.path}
                  to={s.path}
                  data-testid={`landing-related-${s.path}`}
                  className="group inline-flex items-center gap-2 font-serif text-xl md:text-2xl text-ink hover:text-bronze transition-colors"
                >
                  {s.label}
                  <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="py-20 md:py-28 bg-secondary border-t border-border">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="max-w-3xl">
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight text-ink leading-[1.1]">{c.ctaTitle}</h2>
            <p className="mt-5 text-base md:text-lg text-stone">{c.ctaText}</p>
            <Link
              to="/contact"
              data-testid="landing-final-cta"
              className="mt-9 inline-flex items-center gap-2 rounded-full bg-bronze text-paper px-8 py-4 text-sm font-semibold hover:bg-[#f0be6e] transition-colors duration-300"
            >
              {contactLabel} <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
